import React, { useEffect, useState } from 'react';
import { Handle, Position, type NodeProps } from '@xyflow/react';
import { GitBranch, GripVertical, Plus, Trash2, Zap } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Textarea } from '../ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import AutomationActionParamsEditor from './AutomationActionParamsEditor';
import CategoryAutocompleteInput, { type CategoryOption } from './CategoryAutocompleteInput';
import {
  ACTION_TYPES,
  CONDITION_FIELDS,
  TICKET_CONDITION_FIELDS,
  OPERATORS,
  EVENT_TYPES,
  COMMON_PROFILE_FIELD_KEYS,
} from './automationConstants';
import { NODE_W, SETTINGS_NODE_W } from './automationFlowLayout';
import { visionRoutingAPI } from '../../services/api';
import { useFormConfig, useFormMeta, type CatalogEntry, type FormMeta } from './formMeta';

/**
 * 2026-09-24 (Docs/RELEASE_NOTES_2026-09-24_FORMS.md): the
 * form conditions (registration status, contact field, times asked,
 * days since last sent, form / kind on the form_* events)
 * come from the backend catalog, merged after the static lists above.
 * Prefixed fields (e.g. "contact_field.") are completed by a key picker,
 * so the stored field_name is e.g. "contact_field.community".
 */
const formConditionFields = (meta: FormMeta | null) =>
  (meta?.condition_fields || []).map((f) => ({ value: f.value, label: f.label }));

const prefixOf = (meta: FormMeta | null, fieldName: string): CatalogEntry | undefined =>
  (meta?.condition_fields || []).find((f) => f.prefix && !!fieldName && fieldName.startsWith(f.value));

const FormKeyPicker: React.FC<{
  prefix: CatalogEntry;
  fieldName: string;
  onChange: (fieldName: string) => void;
}> = ({ prefix, fieldName, onChange }) => {
  const cfg = useFormConfig();
  const current = fieldName.slice(prefix.value.length);
  const isFieldPrefix = prefix.value.startsWith('contact_field');
  const options = isFieldPrefix
    ? (cfg?.fields || []).map((f) => ({ key: f.key, label: f.label }))
    : (cfg?.forms || []).filter((qs) => !qs.archived_at).map((qs) => ({ key: qs.key, label: qs.name }));
  return (
    <Select value={current || ''} onValueChange={(v) => onChange(`${prefix.value}${v}`)}>
      <SelectTrigger className="h-7 text-xs">
        <SelectValue placeholder={isFieldPrefix ? 'Which field' : 'Which form'} />
      </SelectTrigger>
      <SelectContent>
        {options.map((o) => <SelectItem key={o.key} value={o.key}>{o.label}</SelectItem>)}
        {current && !options.some((o) => o.key === current) && <SelectItem value={current}>{current}</SelectItem>}
      </SelectContent>
    </Select>
  );
};

/**
 * 2026-09-10 (plan 08 Phase 1d, resolving the plan's own Open Decision
 * #2 in favor of a friendly UI over raw JSON): the Signals condition
 * field's value editor -- toggleable chips sourced from this account's
 * REAL configured signals (Automation -> Signals & Image Routing), not a
 * free-text box. Stores `expected_value` as a JSON array string, exactly
 * what automation_engine.py's contains_any/contains_all already expect
 * (`json.loads(expected)` when `expected` is a str) -- an admin never
 * hand-types JSON to use this.
 */
const SignalMultiSelect: React.FC<{ value: string; onChange: (v: string) => void }> = ({ value, onChange }) => {
  const [signals, setSignals] = useState<Array<{ key: string }>>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    visionRoutingAPI
      .getSignals()
      .then((data: Array<{ key: string }>) => {
        if (!cancelled) setSignals(data);
      })
      .catch(() => {
        if (!cancelled) setSignals([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  let selected: string[] = [];
  try {
    const parsed = value ? JSON.parse(value) : [];
    selected = Array.isArray(parsed) ? parsed : [];
  } catch {
    selected = [];
  }

  const toggle = (key: string) => {
    const next = selected.includes(key) ? selected.filter((k) => k !== key) : [...selected, key];
    onChange(JSON.stringify(next));
  };

  if (loading) {
    return <span className="text-xs text-muted-foreground">Loading signals…</span>;
  }
  if (signals.length === 0) {
    return (
      <span className="text-xs text-muted-foreground">
        No signals configured yet — add one under Automation → Signals &amp; Image Routing.
      </span>
    );
  }
  return (
    <div className="flex flex-1 flex-wrap gap-1">
      {signals.map((s) => (
        <Badge
          key={s.key}
          variant={selected.includes(s.key) ? 'default' : 'outline'}
          className="cursor-pointer text-[10px] font-normal"
          onClick={() => toggle(s.key)}
        >
          {s.key}
        </Badge>
      ))}
    </div>
  );
};

/** 2026-09-01 (live feedback — "everything should be in canvas"): the
 * rule-level settings (name, trigger event, description, enable, priority,
 * stop_processing) that used to be a separate form above the canvas now
 * live inside the Trigger node itself (TriggerFlowNode below) — attached to
 * every node's `data` identically to FlowSharedActions/FlowConditionActions,
 * set once in AutomationFlowEditor.tsx. */
export interface FlowSettingsActions {
  formData: {
    name: string;
    description: string;
    trigger_event: string;
    enabled: boolean;
    priority: number;
    stop_processing: boolean;
  };
  onChangeField: (key: string, value: any) => void;
}

/** Shared callback/context bag attached identically to every node's `data`
 * by AutomationFlowEditor.tsx — see that file for what each does. */
export interface FlowSharedActions {
  isTicketEvent: boolean;
  agents: any[];
  teams: any[];
  labels: any[];
  categories: CategoryOption[];
  templates: Array<{ id: number; name: string; language?: string }>;
  channels: Array<{ id: number; name: string }>;
  onChangeActionType: (id: string, actionType: string) => void;
  onChangeActionParam: (id: string, key: string, value: any) => void;
  onDeleteAction: (id: string, isBranch: boolean) => void;
  onConvertToBranch: (id: string) => void;
  onInsertActionAfter: (id: string) => void;
  onChangeBranchField: (branchId: string, field: string) => void;
  onAddCase: (branchId: string) => void;
  onRemoveCase: (branchId: string, caseId: string) => void;
  onUpdateCase: (branchId: string, caseId: string, patch: Record<string, any>) => void;
  onAddActionTo: (branchId: string | null, caseId: string | null) => void;
  // 2026-09-24: the rule's trigger (Send form warns when "Required"
  // can't pause on it).
  triggerEvent?: string;
}

/** Callback bag for the Conditions node — separate from FlowSharedActions
 * since it edits `conditions` (rule-level state in AutomationRuleDialog),
 * not the `actions` tree. */
export interface FlowConditionActions {
  isTicketEvent: boolean;
  categories: CategoryOption[];
  onAddCondition: () => void;
  onRemoveCondition: (index: number) => void;
  onChangeCondition: (index: number, field: string, value: any) => void;
}

// nopan: without this, React Flow's own pane-panning gesture can hijack a
// click-drag that started inside a node instead of leaving it to the node
// (or, for a node with no drag handle at all, just doing nothing). Does not
// affect node dragging itself.
//
// 2026-09-01 correction: `nodrag` used to live here too, applied to every
// node type's outer wrapper. `nodrag` blocks drag-start for its ENTIRE
// subtree — there is no way for a class on a child to "undo" it — so it was
// silently blocking the freeform node dragging AutomationFlowEditor.tsx
// enables for action/branch/case nodes (draggable: true had no effect; the
// pane panned because that gesture starts on the pane, never on the node).
// Fixed by removing it here and giving action/branch/case nodes their own
// small `.automation-drag-handle` strip instead (see those components
// below), wired via each node's `dragHandle` prop in AutomationFlowEditor's
// nodes useMemo — dragging is scoped to that strip specifically, so every
// Button/Select/Input elsewhere in the node keeps working untouched (no
// competing pointer-capture, since the drag system never intercepts them at
// all once a dragHandle selector is set).
const cardBase = 'nopan rounded-lg border border-border bg-card text-card-foreground shadow-sm';

// Small non-interactive grip strip — the only part of an action/branch/case
// node that actually starts a drag (see cardBase's note above). Negative
// margins bleed it to the card's own edges/radius — `compact` matches the
// shallower px-3 py-2 padding CaseFlowNode uses (vs. everything else's p-3).
const DragHandle: React.FC<{ label: string; compact?: boolean }> = ({ label, compact }) => (
  <div
    className={`automation-drag-handle -mx-3 ${compact ? '-mt-2' : '-mt-3'} mb-2 flex cursor-grab items-center gap-1.5 rounded-t-lg border-b border-border/60 bg-muted/40 px-3 py-1 text-[10px] font-medium uppercase tracking-wide text-muted-foreground active:cursor-grabbing`}
  >
    <GripVertical className="h-3 w-3" />
    {label}
  </div>
);

export const ConditionFlowNode: React.FC<NodeProps> = ({ data }) => {
  const conditions = ((data as any).conditions || []) as any[];
  const actions = (data as any).conditionsActions as FlowConditionActions;
  const meta = useFormMeta();
  const fields = [...(actions.isTicketEvent ? TICKET_CONDITION_FIELDS : CONDITION_FIELDS), ...formConditionFields(meta)];
  return (
    <div style={{ width: NODE_W }} className={`${cardBase} p-3`}>
      <Handle type="target" position={Position.Top} />
      <DragHandle label="Conditions" />
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Conditions
        </span>
        <Button type="button" variant="ghost" size="sm" className="h-6 gap-1 px-1.5 text-xs" onClick={actions.onAddCondition}>
          <Plus className="h-3 w-3" /> Add
        </Button>
      </div>
      <div className="mt-2 space-y-2">
        {conditions.map((condition, index) => (
          <div key={index} className="space-y-1.5 rounded-md border border-border/60 bg-muted/10 p-2">
            {index > 0 && (
              <Select
                value={condition.group_operator || 'AND'}
                onValueChange={(v) => actions.onChangeCondition(index, 'group_operator', v)}
              >
                <SelectTrigger className="h-6 w-[76px] text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="AND">AND</SelectItem>
                  <SelectItem value="OR">OR</SelectItem>
                </SelectContent>
              </Select>
            )}
            <Select
              value={prefixOf(meta, condition.field_name)?.value || condition.field_name}
              onValueChange={(v) => actions.onChangeCondition(index, 'field_name', v)}
            >
              <SelectTrigger className="h-7 text-xs"><SelectValue placeholder="Field" /></SelectTrigger>
              <SelectContent>
                {fields.map((f) => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}
              </SelectContent>
            </Select>
            {prefixOf(meta, condition.field_name) && (
              <FormKeyPicker
                prefix={prefixOf(meta, condition.field_name) as CatalogEntry}
                fieldName={condition.field_name}
                onChange={(v) => actions.onChangeCondition(index, 'field_name', v)}
              />
            )}
            <Select value={condition.operator} onValueChange={(v) => actions.onChangeCondition(index, 'operator', v)}>
              <SelectTrigger className="h-7 text-xs"><SelectValue placeholder="Operator" /></SelectTrigger>
              <SelectContent>
                {OPERATORS.map((op) => <SelectItem key={op.value} value={op.value}>{op.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <div className="flex items-center gap-1">
              {(meta?.condition_fields || []).some((f) => f.value === condition.field_name && f.values) ? (
                <Select
                  value={condition.expected_value || ''}
                  onValueChange={(v) => actions.onChangeCondition(index, 'expected_value', v)}
                >
                  <SelectTrigger className="h-7 flex-1 text-xs"><SelectValue placeholder="Value" /></SelectTrigger>
                  <SelectContent>
                    {((meta?.condition_fields || []).find((f) => f.value === condition.field_name)?.values || []).map((v) => (
                      <SelectItem key={v} value={v}>{v.replace(/_/g, ' ')}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : condition.field_name === 'category_id' ? (
                <CategoryAutocompleteInput
                  className="h-7 flex-1 text-xs"
                  value={condition.expected_value || ''}
                  onChange={(v) => actions.onChangeCondition(index, 'expected_value', v)}
                  categories={actions.categories}
                />
              ) : condition.field_name === 'signals' ? (
                <SignalMultiSelect
                  value={condition.expected_value || ''}
                  onChange={(v) => actions.onChangeCondition(index, 'expected_value', v)}
                />
              ) : condition.field_name === 'field_key' ? (
                // 2026-09-07: suggests common profile field keys (name, age,
                // id_card_photo, ...) but still accepts anything typed --
                // same <datalist> pattern as CategoryAutocompleteInput above,
                // not a restriction (field_key is free text by design).
                <>
                  <Input
                    className="h-7 flex-1 text-xs"
                    list={`condition-${index}-field-key-suggestions`}
                    value={condition.expected_value || ''}
                    onChange={(e) => actions.onChangeCondition(index, 'expected_value', e.target.value)}
                    placeholder="e.g. name, age, id_card_photo"
                  />
                  <datalist id={`condition-${index}-field-key-suggestions`}>
                    {COMMON_PROFILE_FIELD_KEYS.map((key) => (
                      <option key={key} value={key} />
                    ))}
                  </datalist>
                </>
              ) : (
                <Input
                  className="h-7 flex-1 text-xs"
                  value={condition.expected_value || ''}
                  onChange={(e) => actions.onChangeCondition(index, 'expected_value', e.target.value)}
                  placeholder={
                    // 2026-09-09: this field isn't evaluated like a normal
                    // condition (see automationConstants.ts's own comment) --
                    // the operator picked above is cosmetic, only this
                    // number-of-minutes value is actually used.
                    condition.field_name === 'inactivity_minutes'
                      ? 'Minutes to wait, e.g. 1'
                      : 'Value…'
                  }
                />
              )}
              {/* 2026-09-07: no longer disabled at 1 remaining -- a rule can
                  have zero conditions ("trigger on the event alone");
                  automation_engine.py's _conditions_match already treats an
                  empty list as "always match" as a first-class case, and the
                  backend schema no longer requires at least one either. */}
              <Button
                type="button" variant="ghost" size="sm"
                className="h-7 w-7 shrink-0 p-0 text-muted-foreground hover:text-destructive"
                onClick={() => actions.onRemoveCondition(index)}
                aria-label="Remove condition"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        ))}
      </div>
      <Handle type="source" position={Position.Bottom} />
    </div>
  );
};

export const TriggerFlowNode: React.FC<NodeProps> = ({ data }) => {
  const settings = (data as any).settingsActions as FlowSettingsActions;
  const f = settings.formData;
  const meta = useFormMeta();
  const formTriggers: CatalogEntry[] = [...(meta?.checkpoints || []), ...(meta?.events || [])];
  const selectedFormTrigger = formTriggers.find((t) => t.value === f.trigger_event);
  return (
    <div style={{ width: SETTINGS_NODE_W }} className={`${cardBase} border-primary/40 bg-primary/5 px-3.5 py-3`}>
      <DragHandle label="Trigger" />
      <div className="flex items-center gap-2 text-sm font-medium">
        <Zap className="h-4 w-4 text-primary" />
        Trigger &amp; Settings
      </div>

      <div className="mt-3 grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label className="text-xs">Rule name</Label>
          <Input
            value={f.name}
            onChange={(e) => settings.onChangeField('name', e.target.value)}
            placeholder="e.g. VIP auto-assign"
          />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Trigger event</Label>
          <Select value={f.trigger_event} onValueChange={(v) => settings.onChangeField('trigger_event', v)}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {EVENT_TYPES.map((event) => (
                <SelectItem key={event.value} value={event.value}>
                  {event.label}
                </SelectItem>
              ))}
              {(meta?.checkpoints || []).map((cp) => (
                <SelectItem key={cp.value} value={cp.value}>
                  Checkpoint · {cp.label}
                </SelectItem>
              ))}
              {(meta?.events || []).map((ev) => (
                <SelectItem key={ev.value} value={ev.value}>
                  {ev.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {selectedFormTrigger?.description && (
          <p className="col-span-2 -mt-1 text-[11px] text-muted-foreground">
            {selectedFormTrigger.can_hold && (
              <Badge variant="outline" className="mr-1.5 border-amber-400/70 px-1.5 py-0 text-[10px] text-amber-700 dark:text-amber-400">
                can pause
              </Badge>
            )}
            {selectedFormTrigger.description}
          </p>
        )}
        <div className="col-span-2 space-y-1.5">
          <Label className="text-xs">Description</Label>
          <Textarea
            rows={2}
            value={f.description}
            onChange={(e) => settings.onChangeField('description', e.target.value)}
            placeholder="Optional notes for your team"
          />
        </div>
        <div className="flex items-center justify-between rounded-md border border-border/60 bg-background px-2.5 py-2">
          <span className="text-xs font-medium">Enable rule</span>
          <Switch checked={f.enabled} onCheckedChange={(v) => settings.onChangeField('enabled', v)} />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Priority</Label>
          <Input
            type="number"
            value={f.priority}
            onChange={(e) => settings.onChangeField('priority', Number(e.target.value) || 0)}
          />
        </div>
        <div className="col-span-2 flex items-center justify-between rounded-md border border-border/60 bg-background px-2.5 py-2">
          <span className="text-xs font-medium">Stop processing — skip lower-priority rules once this one runs</span>
          <Switch checked={f.stop_processing} onCheckedChange={(v) => settings.onChangeField('stop_processing', v)} />
        </div>
      </div>

      <Handle type="source" position={Position.Bottom} />
    </div>
  );
};

export const ActionFlowNode: React.FC<NodeProps> = ({ id, data }) => {
  const node = (data as any).node;
  const actions = (data as any).actions as FlowSharedActions;
  return (
    <div style={{ width: NODE_W }} className={`${cardBase} p-3`}>
      <Handle type="target" position={Position.Top} />
      <DragHandle label="Action" />
      <div className="flex items-start justify-between gap-1">
        <Select value={node.action_type} onValueChange={(v) => actions.onChangeActionType(id, v)}>
          <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            {ACTION_TYPES.map((t) => (
              <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="mt-2">
        <AutomationActionParamsEditor
          actionType={node.action_type}
          params={node.action_parameters || {}}
          onChange={(key, value) => actions.onChangeActionParam(id, key, value)}
          isTicketEvent={actions.isTicketEvent}
          agents={actions.agents}
          teams={actions.teams}
          labels={actions.labels}
          templates={actions.templates}
          channels={actions.channels}
          idPrefix={`flow-${id}`}
          triggerEvent={actions.triggerEvent}
        />
      </div>
      <div className="mt-2.5 flex items-center gap-1.5 border-t border-border/60 pt-2">
        <Button type="button" variant="ghost" size="sm" className="h-6 gap-1 px-1.5 text-xs" onClick={() => actions.onInsertActionAfter(id)}>
          <Plus className="h-3 w-3" /> Action
        </Button>
        <Button type="button" variant="ghost" size="sm" className="h-6 gap-1 px-1.5 text-xs" onClick={() => actions.onConvertToBranch(id)}>
          <GitBranch className="h-3 w-3" /> Branch
        </Button>
        <Button
          type="button" variant="ghost" size="sm"
          className="ml-auto h-6 w-6 p-0 text-muted-foreground hover:text-destructive"
          onClick={() => actions.onDeleteAction(id, false)}
          aria-label="Delete action"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>
      <Handle type="source" position={Position.Bottom} />
    </div>
  );
};

export const BranchFlowNode: React.FC<NodeProps> = ({ id, data }) => {
  const node = (data as any).node;
  const actions = (data as any).actions as FlowSharedActions;
  const params = node.action_parameters || {};
  const fields = actions.isTicketEvent ? TICKET_CONDITION_FIELDS : CONDITION_FIELDS;
  return (
    <div style={{ width: NODE_W }} className={`${cardBase} border-amber-400/60 bg-amber-50/60 p-3 dark:bg-amber-950/20`}>
      <Handle type="target" position={Position.Top} />
      <DragHandle label="Branch" />
      <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-amber-700 dark:text-amber-400">
        <GitBranch className="h-3.5 w-3.5" /> Branch on
      </div>
      <div className="mt-2">
        <Select value={params.field || ''} onValueChange={(v) => actions.onChangeBranchField(id, v)}>
          <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Select field" /></SelectTrigger>
          <SelectContent>
            {fields.map((f) => (
              <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="mt-2.5 flex items-center gap-1.5 border-t border-amber-400/40 pt-2">
        <Button type="button" variant="ghost" size="sm" className="h-6 gap-1 px-1.5 text-xs" onClick={() => actions.onAddCase(id)}>
          <Plus className="h-3 w-3" /> Case
        </Button>
        <Button
          type="button" variant="ghost" size="sm"
          className="ml-auto h-6 w-6 p-0 text-muted-foreground hover:text-destructive"
          onClick={() => actions.onDeleteAction(id, true)}
          aria-label="Delete branch"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>
      <Handle type="source" position={Position.Bottom} />
    </div>
  );
};

export const CaseFlowNode: React.FC<NodeProps> = ({ data }) => {
  const { branchId, caseId, isDefault, operator, expected_value, field, actions } = data as any as {
    branchId: string; caseId: string; isDefault: boolean; operator?: string; expected_value?: any; field?: string;
    actions: FlowSharedActions;
  };
  if (isDefault) {
    return (
      <div style={{ width: NODE_W }} className={`${cardBase} border-dashed bg-muted/30 px-3 py-2`}>
        <Handle type="target" position={Position.Top} />
        <DragHandle label="Case" compact />
        <p className="text-xs font-medium text-muted-foreground">Else (no case matched)</p>
        <Handle type="source" position={Position.Bottom} />
      </div>
    );
  }
  return (
    <div style={{ width: NODE_W }} className={`${cardBase} px-3 py-2`}>
      <Handle type="target" position={Position.Top} />
      <DragHandle label="Case" />
      <div className="flex items-center gap-1.5">
        <Select
          value={operator || 'equal_to'}
          onValueChange={(v) => actions.onUpdateCase(branchId, caseId, { operator: v })}
        >
          <SelectTrigger className="h-7 flex-1 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            {OPERATORS.map((op) => (
              <SelectItem key={op.value} value={op.value}>{op.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          type="button" variant="ghost" size="sm"
          className="h-7 w-7 shrink-0 p-0 text-muted-foreground hover:text-destructive"
          onClick={() => actions.onRemoveCase(branchId, caseId)}
          aria-label="Remove case"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>
      {field === 'category_id' ? (
        <CategoryAutocompleteInput
          className="mt-1.5 h-7 text-xs"
          value={expected_value ?? ''}
          onChange={(v) => actions.onUpdateCase(branchId, caseId, { expected_value: v })}
          categories={actions.categories}
        />
      ) : (
        <Input
          className="mt-1.5 h-7 text-xs"
          value={expected_value ?? ''}
          onChange={(e) => actions.onUpdateCase(branchId, caseId, { expected_value: e.target.value })}
          placeholder="Value to compare…"
        />
      )}
      <Handle type="source" position={Position.Bottom} />
    </div>
  );
};

export const AddActionFlowNode: React.FC<NodeProps> = ({ data }) => {
  const { branchId, caseId, actions } = data as any as {
    branchId: string | null; caseId: string | null; actions: FlowSharedActions;
  };
  return (
    <div style={{ width: NODE_W }} className="nodrag nopan">
      <Handle type="target" position={Position.Top} />
      <button
        type="button"
        onClick={() => actions.onAddActionTo(branchId, caseId)}
        className="flex w-full items-center justify-center gap-1.5 rounded-lg border border-dashed border-border py-2.5 text-xs text-muted-foreground hover:border-primary hover:text-primary"
      >
        <Plus className="h-3.5 w-3.5" /> Add action
      </button>
    </div>
  );
};

export const automationFlowNodeTypes = {
  trigger: TriggerFlowNode,
  condition: ConditionFlowNode,
  action: ActionFlowNode,
  branch: BranchFlowNode,
  case: CaseFlowNode,
  addAction: AddActionFlowNode,
};
